import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Bookmark, CalendarClock, ExternalLink, Pencil, Trash2 } from 'lucide-react';
import { Botao, Selo } from '../../componentes/base';
import { Caixas } from '../../componentes/caixas';
import { Estado } from '../../componentes/estados';
import { Alternador, BuscaRemota, Entrada, EntradaSensivel, Rotulo, Selecao } from '../../componentes/formulario';
import { Confirmar, Modal } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { api, del, get, getTodos, mensagemDeErro, patch, post } from '../../nucleo/api';
import { hojeNoFuso } from '../../componentes/periodo/periodo';
import { useSessao } from '../../nucleo/sessao';
import { dataHora, nomePessoa, textoLivre } from '../../nucleo/formato';

/**
 * E1 (lote 09/10, item 6, opção A): "Meus relatórios". Um relatório salvo guarda o relatório fixo, os filtros da tela e o
 * período relativo opcional; abrir é ir ao relatório com esses parâmetros, e o relatório roda com as permissões e a empresa
 * de QUEM ABRE (o servidor confere de novo). Compartilhar (papéis, pessoas ou todo o workspace) e agendar o envio por
 * e-mail são de quem criou. Tudo com os componentes existentes (cartão da página de entrada, Modal, Selecao, Alternador).
 */

export const PERIODOS = ['mes_atual', 'mes_passado', 'ultimos_30', 'trimestre_atual', 'ano_atual'] as const;
export type Periodo = (typeof PERIODOS)[number];
export const COM_PERIODO = new Set(['dre', 'fluxo', 'extrato', 'centros', 'parceiros', 'margem', 'quadro', 'folha', 'painel_grupo']);

/** Aba de um relatório (a chave da URL usa hífen: `painel_grupo` mora em `painel-grupo`). */
export const abaDoRelatorio = (r: string) => r.replaceAll('_', '-');
type Compartilhamento = 'privado' | 'papeis' | 'pessoas' | 'workspace';

export interface Salvo {
  id: string;
  nome: string;
  descricao: string;
  relatorio: string;
  parametros: Record<string, unknown>;
  periodo: Periodo | null;
  compartilhamento: Compartilhamento;
  papeis: string[];
  pessoas: string[];
  agenda: { frequencia: 'diaria' | 'semanal' | 'mensal'; dia: number | null; hora: number; destinatarios: string[]; mascarar: boolean; ultimo_envio: string | null } | null;
  meu: boolean;
  autor: string | null;
}

const ultimoDia = (iso: string) => {
  const [a, m] = iso.split('-').map(Number) as [number, number];
  return new Date(Date.UTC(a, m, 0)).toISOString().slice(0, 10);
};

/** As datas do período relativo no dia de hoje (a mesma regra do servidor, `datasDoPeriodo`). */
export function datasDoPeriodo(p: Periodo, hoje: string): { de: string; ate: string } {
  const [a, m] = hoje.split('-').map(Number) as [number, number];
  const mes = (ano: number, mm: number) => `${ano}-${String(mm).padStart(2, '0')}-01`;
  if (p === 'mes_atual') return { de: mes(a, m), ate: ultimoDia(hoje) };
  if (p === 'mes_passado') {
    const de = m === 1 ? mes(a - 1, 12) : mes(a, m - 1);
    return { de, ate: ultimoDia(de) };
  }
  if (p === 'ultimos_30') return { de: new Date(Date.parse(`${hoje}T00:00:00Z`) - 29 * 86_400_000).toISOString().slice(0, 10), ate: hoje };
  if (p === 'trimestre_atual') {
    const ini = Math.floor((m - 1) / 3) * 3 + 1;
    return { de: mes(a, ini), ate: ultimoDia(mes(a, ini + 2)) };
  }
  return { de: `${a}-01-01`, ate: `${a}-12-31` };
}

/** Endereço do relatório com os parâmetros do salvo (o período relativo vira as datas de hoje). */
export function urlDoSalvo(s: Pick<Salvo, 'relatorio' | 'parametros' | 'periodo'>, hoje: string): string {
  const p = { ...s.parametros, ...(s.periodo && COM_PERIODO.has(s.relatorio) ? datasDoPeriodo(s.periodo, hoje) : {}) };
  const busca = new URLSearchParams(Object.entries(p).filter(([, v]) => v !== undefined && v !== null && v !== '').map(([k, v]) => [k, String(v)]));
  const q = busca.toString();
  return `/relatorios/relatorios/${abaDoRelatorio(s.relatorio)}${q ? `?${q}` : ''}`;
}

interface Membro {
  id: string;
  nome: string;
}

/**
 * Caixas de seleção (o padrão das listas de membros do sistema). Review r37 visual (B1): `fieldset` com `legend`. Review r38
 * (SPEC-B2, VIS-B1): mora em `componentes/caixas.tsx`, o mesmo do "Compartilhar com" das visões da `Tabela`.
 */
export { Caixas };

function useMembros(ativo: boolean) {
  return useQuery({ queryKey: ['membros', 'todos'], queryFn: () => getTodos<Membro>('/membros'), enabled: ativo, staleTime: 60_000 });
}

/** Salvar (novo) ou editar um relatório salvo: nome, descrição, período e com quem compartilhar. */
export function ModalSalvo({ relatorio, parametros, atual, aoFechar }: { relatorio: string; parametros: Record<string, unknown>; atual?: Salvo; aoFechar: () => void }) {
  const { t } = useTranslation('relatorios');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const toast = useToast();
  const qc = useQueryClient();
  const podeCompartilhar = s.pode('relatorios.compartilhar');
  const [nome, setNome] = useState(atual?.nome ?? '');
  const [descricao, setDescricao] = useState(atual?.descricao ?? '');
  const [periodo, setPeriodo] = useState<Periodo | ''>(atual?.periodo ?? '');
  const [comp, setComp] = useState<Compartilhamento>(atual?.compartilhamento ?? 'privado');
  const [papeis, setPapeis] = useState<string[]>(atual?.papeis ?? []);
  const [pessoas, setPessoas] = useState<string[]>(atual?.pessoas ?? []);
  const membros = useMembros(comp === 'pessoas');
  const listaPapeis = useQuery({ queryKey: ['/relatorios/papeis'], queryFn: () => get<{ itens: Membro[] }>('/relatorios/papeis'), enabled: comp === 'papeis' && podeCompartilhar });
  const corpo = { nome, descricao, relatorio, parametros, periodo: periodo || null, compartilhamento: comp, papeis, pessoas };
  const m = useMutation({
    mutationFn: () => (atual ? patch(`/relatorios/salvos/${atual.id}`, corpo) : post('/relatorios/salvos', { id: crypto.randomUUID(), ...corpo })),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['/relatorios/salvos'] });
      toast.sucesso(t('salvos.salvo'));
      aoFechar();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      titulo={atual ? t('salvos.editar') : t('salvos.salvar')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" disabled={!nome.trim()} carregando={m.isPending} onClick={() => m.mutate()}>
            {tn('comum.salvar')}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Rotulo id="salvo-nome" rotulo={t('salvos.nome')} obrigatorio>
          <Entrada id="salvo-nome" value={nome} onChange={(e) => setNome(e.target.value)} maxLength={120} />
        </Rotulo>
        <Rotulo id="salvo-descricao" rotulo={t('salvos.descricao')}>
          <EntradaSensivel mascara={null} livre id="salvo-descricao" value={descricao} onChange={(e) => setDescricao(e.target.value)} maxLength={500} />
        </Rotulo>
        {COM_PERIODO.has(relatorio) && (
          <Rotulo id="salvo-periodo" rotulo={t('salvos.periodo')} ajuda={t('salvos.periodoAjuda')}>
            <Selecao
              id="salvo-periodo"
              value={periodo}
              onChange={(e) => setPeriodo(e.target.value as Periodo | '')}
              opcoes={[{ valor: '', rotulo: t('salvos.periodos.fixo') }, ...PERIODOS.map((p) => ({ valor: p, rotulo: t(`salvos.periodos.${p}`) }))]}
            />
          </Rotulo>
        )}
        {podeCompartilhar && (
          <Rotulo id="salvo-compartilhar" rotulo={t('salvos.compartilhar')} ajuda={t('salvos.compartilharAjuda')}>
            <Selecao
              id="salvo-compartilhar"
              value={comp}
              onChange={(e) => setComp(e.target.value as Compartilhamento)}
              opcoes={(['privado', 'papeis', 'pessoas', 'workspace'] as const).map((c) => ({ valor: c, rotulo: t(`salvos.compartilhamentos.${c}`) }))}
            />
          </Rotulo>
        )}
        {comp === 'papeis' && podeCompartilhar && (
          <Estado consulta={listaPapeis}>{(d) => <Caixas id="salvo-papeis" rotulo={t('salvos.papeis')} itens={d.itens} valor={papeis} aoMudar={setPapeis} />}</Estado>
        )}
        {comp === 'pessoas' && podeCompartilhar && (
          <Estado consulta={membros}>{(d) => <Caixas id="salvo-pessoas" rotulo={t('salvos.pessoas')} pessoas itens={d.itens.filter((x) => x.id !== s.usuario.id)} valor={pessoas} aoMudar={setPessoas} />}</Estado>
        )}
      </div>
    </Modal>
  );
}

const DIAS_SEMANA = [0, 1, 2, 3, 4, 5, 6];

/** Agendamento por e-mail: frequência, dia, hora e destinatários (cada um recebe com as próprias permissões). */
function ModalAgenda({ salvo, aoFechar }: { salvo: Salvo; aoFechar: () => void }) {
  const { t } = useTranslation('relatorios');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const toast = useToast();
  const qc = useQueryClient();
  const a = salvo.agenda;
  const [frequencia, setFrequencia] = useState<'' | 'diaria' | 'semanal' | 'mensal'>(a?.frequencia ?? 'semanal');
  const [dia, setDia] = useState<number>(a?.dia ?? 1);
  const [hora, setHora] = useState<number>(a?.hora ?? 8);
  const [destinatarios, setDestinatarios] = useState<string[]>(a?.destinatarios ?? [s.usuario.id]);
  const [mascarar, setMascarar] = useState(a?.mascarar ?? false);
  const membros = useMembros(true);
  const outros = s.pode('relatorios.compartilhar');
  const m = useMutation({
    mutationFn: () => api('PUT', `/relatorios/salvos/${salvo.id}/agenda`, frequencia ? { frequencia, dia: frequencia === 'diaria' ? null : dia, hora, destinatarios, mascarar } : { frequencia: null }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['/relatorios/salvos'] });
      toast.sucesso(frequencia ? t('salvos.agendado') : t('salvos.desagendado'));
      aoFechar();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      titulo={t('salvos.agendarTitulo', { nome: salvo.nome })}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" carregando={m.isPending} disabled={!!frequencia && !destinatarios.length} onClick={() => m.mutate()}>
            {tn('comum.salvar')}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm text-tinta-3">{t('salvos.agendaAjuda')}</p>
        <Rotulo id="agenda-frequencia" rotulo={t('salvos.frequencia')}>
          <Selecao
            id="agenda-frequencia"
            value={frequencia}
            onChange={(e) => setFrequencia(e.target.value as typeof frequencia)}
            opcoes={[{ valor: '', rotulo: t('salvos.frequencias.nenhuma') }, ...(['diaria', 'semanal', 'mensal'] as const).map((f) => ({ valor: f, rotulo: t(`salvos.frequencias.${f}`) }))]}
          />
        </Rotulo>
        {frequencia === 'semanal' && (
          <Rotulo id="agenda-dia" rotulo={t('salvos.diaSemana')}>
            <Selecao id="agenda-dia" value={String(dia)} onChange={(e) => setDia(Number(e.target.value))} opcoes={DIAS_SEMANA.map((d) => ({ valor: String(d), rotulo: t(`salvos.dias.${d}`) }))} />
          </Rotulo>
        )}
        {frequencia === 'mensal' && (
          <Rotulo id="agenda-dia" rotulo={t('salvos.diaMes')}>
            <Selecao id="agenda-dia" value={String(dia)} onChange={(e) => setDia(Number(e.target.value))} opcoes={Array.from({ length: 28 }, (_, i) => ({ valor: String(i + 1), rotulo: String(i + 1) }))} />
          </Rotulo>
        )}
        {frequencia && (
          <>
            <Rotulo id="agenda-hora" rotulo={t('salvos.hora')}>
              <Selecao id="agenda-hora" value={String(hora)} onChange={(e) => setHora(Number(e.target.value))} opcoes={Array.from({ length: 24 }, (_, h) => ({ valor: String(h), rotulo: `${String(h).padStart(2, '0')}:00` }))} />
            </Rotulo>
            <Estado consulta={membros}>
              {(d) => <Caixas id="agenda-destinatarios" rotulo={t('salvos.destinatarios')} pessoas itens={outros ? d.itens : d.itens.filter((x) => x.id === s.usuario.id)} valor={destinatarios} aoMudar={setDestinatarios} />}
            </Estado>
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-sm font-medium text-tinta-1">{t('salvos.mascarar')}</p>
                <p className="text-xs text-tinta-3">{t('salvos.mascararAjuda')}</p>
              </div>
              <Alternador id="agenda-mascarar" rotulo={t('salvos.mascarar')} marcado={mascarar} aoMudar={setMascarar} />
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

/** E1: os relatórios que vão ao Portal do cliente (a mesma regra do servidor, `definicaoNoPortal`): os que são por cliente. */
export function vaiAoPortal(s: Pick<Salvo, 'relatorio' | 'parametros'>): boolean {
  if (s.relatorio === 'inadimplencia') return true;
  return s.relatorio === 'parceiros' && (s.parametros.lado ?? 'clientes') === 'clientes' && !s.parametros.grupo;
}

interface Publicacao {
  id: string;
  parceiro_id: string;
  parceiro_nome: string;
  titulo: string;
  publicado_em: string;
  aberto_em: string | null;
  publicado_por: string | null;
}

/**
 * E1: "Publicar no Portal". O cliente escolhido abre o relatório sozinho no Portal, sempre atualizado e só com as faturas
 * dele; a lista mostra para quem está publicado e a última abertura, e despublicar corta o acesso na hora.
 */
function ModalPortal({ salvo, aoFechar }: { salvo: Salvo; aoFechar: () => void }) {
  const { t } = useTranslation('relatorios');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const toast = useToast();
  const qc = useQueryClient();
  const [cliente, setCliente] = useState<string | null>(null);
  const [tirar, setTirar] = useState<Publicacao | null>(null);
  const chave = ['/relatorios/salvos', salvo.id, 'portal'];
  const lista = useQuery({ queryKey: chave, queryFn: () => get<{ itens: Publicacao[] }>(`/relatorios/salvos/${salvo.id}/portal`) });
  // Val5 (29/10): publicar o relatório salvo no Portal tem ação própria (`relatorios.publicar_portal`), além de `portal.publicar`.
  const podePublicar = salvo.meu && s.pode('relatorios.publicar_portal') && s.pode('portal.publicar') && s.pode('contas.ver');
  const publicar = useMutation({
    mutationFn: () => post(`/relatorios/salvos/${salvo.id}/portal`, { publicacao_id: crypto.randomUUID(), parceiro_id: cliente }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: chave });
      setCliente(null);
      toast.sucesso(t('portal.publicado'));
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const despublicar = useMutation({
    mutationFn: (p: Publicacao) => del(`/relatorios/salvos/${salvo.id}/portal/${p.id}`),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: chave });
      setTirar(null);
      toast.sucesso(t('portal.despublicado'));
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      titulo={t('portal.titulo', { nome: salvo.nome })}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.fechar')}
          </Botao>
          {podePublicar && (
            <Botao variante="primario" disabled={!cliente} carregando={publicar.isPending} onClick={() => publicar.mutate()}>
              {t('portal.publicarBotao')}
            </Botao>
          )}
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm text-tinta-3">
          {t('portal.ajuda')} {t(salvo.relatorio === 'inadimplencia' ? 'portal.tipoPosicao' : 'portal.tipoExtrato')}
        </p>
        {podePublicar && (
          <Rotulo id="portal-cliente" rotulo={t('portal.cliente')}>
            <BuscaRemota id="portal-cliente" def={{ caminho: '/cadastros/parceiros', params: { papel: 'cliente', operacao: true }, rotulo: (i) => String(i.nome) }} valor={cliente} aoMudar={(v) => setCliente(v)} />
          </Rotulo>
        )}
        <section className="flex min-w-0 flex-col gap-2" aria-label={t('portal.publicados')}>
          <h3 className="text-sm font-semibold text-tinta-1">{t('portal.publicados')}</h3>
          <Estado consulta={lista}>
            {(d) =>
              d.itens.length ? (
                <ul className="divide-y divide-borda rounded-lg border border-borda bg-sup-2" data-publicacoes-portal>
                  {d.itens.map((p) => (
                    <li key={p.id} className="flex min-w-0 items-center gap-3 px-3 py-2" data-publicacao={p.id}>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-tinta-1" title={nomePessoa(p.parceiro_nome)}>
                          {nomePessoa(p.parceiro_nome)}
                        </p>
                        {/* Review r37 visual (M2): a linha cortada mostra o texto inteiro no title (a última abertura é o dado da linha). */}
                        {(() => {
                          const datas = `${t('portal.publicadoEm', { data: dataHora(p.publicado_em) })} · ${p.aberto_em ? t('portal.abertoEm', { data: dataHora(p.aberto_em) }) : t('portal.nuncaAberto')}`;
                          return (
                            <p className="truncate text-xs text-tinta-3" title={datas}>
                              {datas}
                            </p>
                          );
                        })()}
                      </div>
                      {s.pode('relatorios.publicar_portal') && s.pode('portal.publicar') && (
                        <Botao variante="fantasma" tamanho="sm" aria-label={t('portal.despublicar')} title={t('portal.despublicar')} icone={<Trash2 className="size-4" />} onClick={() => setTirar(p)} />
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-tinta-3">{t('portal.nenhum')}</p>
              )
            }
          </Estado>
        </section>
      </div>
      <Confirmar
        aberto={!!tirar}
        aoFechar={() => setTirar(null)}
        aoConfirmar={() => tirar && despublicar.mutate(tirar)}
        titulo={t('portal.despublicar')}
        texto={t('portal.despublicarTexto', { titulo: tirar?.titulo ?? '', cliente: tirar ? nomePessoa(tirar.parceiro_nome) : '' })}
        perigoso
        carregando={despublicar.isPending}
      />
    </Modal>
  );
}

/** Um cartão de relatório salvo (o mesmo do cartão de relatório fixo), com as ações de quem criou. */
function CartaoSalvo({ salvo, hoje }: { salvo: Salvo; hoje: string }) {
  const { t } = useTranslation('relatorios');
  const s = useSessao();
  const toast = useToast();
  const qc = useQueryClient();
  const [modal, setModal] = useState<'editar' | 'agenda' | 'excluir' | 'portal' | null>(null);
  // E1: o Portal do cliente instalado, relatório por cliente e quem vê o que está publicado (publicar é só do autor).
  const portal = s.instalado('portal') && vaiAoPortal(salvo) && s.pode('portal.ver');
  const excluir = useMutation({
    mutationFn: () => del(`/relatorios/salvos/${salvo.id}`),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['/relatorios/salvos'] });
      setModal(null);
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  return (
    <li className="min-w-0" data-salvo={salvo.id}>
      <div className="flex h-full min-w-0 items-start gap-4 rounded-xl border border-borda bg-sup-1 p-4">
        <span className="shrink-0 rounded-lg bg-acento-suave p-3 text-acento-forte" aria-hidden>
          <Bookmark className="size-5" />
        </span>
        <div className="min-w-0 flex-1">
          <Link to={urlDoSalvo(salvo, hoje)} className="block text-base font-semibold break-words text-tinta-1 hover:underline">
            {salvo.nome}
          </Link>
          <span className="mt-1 block text-sm break-words text-tinta-2">{salvo.descricao ? textoLivre(salvo.descricao) : t(`abas.${abaDoRelatorio(salvo.relatorio)}`)}</span>
          <span className="mt-2 flex flex-wrap gap-1">
            <Selo>{t(`abas.${abaDoRelatorio(salvo.relatorio)}`)}</Selo>
            {salvo.periodo && <Selo>{t(`salvos.periodos.${salvo.periodo}`)}</Selo>}
            {salvo.meu && salvo.compartilhamento !== 'privado' && <Selo cor="verde">{t(`salvos.compartilhamentos.${salvo.compartilhamento}`)}</Selo>}
            {salvo.agenda && <Selo cor="verde">{t(`salvos.frequencias.${salvo.agenda.frequencia}`)}</Selo>}
            {!salvo.meu && salvo.autor && <Selo>{t('salvos.de', { nome: nomePessoa(salvo.autor) })}</Selo>}
          </span>
        </div>
        {(salvo.meu || portal) && (
          <div className="flex shrink-0 flex-col gap-1">
            {portal && <Botao variante="fantasma" tamanho="sm" aria-label={t('portal.publicar')} title={t('portal.publicar')} icone={<ExternalLink className="size-4" />} onClick={() => setModal('portal')} />}
            {salvo.meu && s.pode('relatorios.salvar') && <Botao variante="fantasma" tamanho="sm" aria-label={t('salvos.editar')} title={t('salvos.editar')} icone={<Pencil className="size-4" />} onClick={() => setModal('editar')} />}
            {salvo.meu && s.pode('relatorios.agendar') && <Botao variante="fantasma" tamanho="sm" aria-label={t('salvos.agendar')} title={t('salvos.agendar')} icone={<CalendarClock className="size-4" />} onClick={() => setModal('agenda')} />}
            {salvo.meu && s.pode('relatorios.salvar') && <Botao variante="fantasma" tamanho="sm" aria-label={t('salvos.excluir')} title={t('salvos.excluir')} icone={<Trash2 className="size-4" />} onClick={() => setModal('excluir')} />}
          </div>
        )}
      </div>
      {modal === 'editar' && <ModalSalvo relatorio={salvo.relatorio} parametros={salvo.parametros} atual={salvo} aoFechar={() => setModal(null)} />}
      {modal === 'agenda' && <ModalAgenda salvo={salvo} aoFechar={() => setModal(null)} />}
      {modal === 'portal' && <ModalPortal salvo={salvo} aoFechar={() => setModal(null)} />}
      <Confirmar
        aberto={modal === 'excluir'}
        aoFechar={() => setModal(null)}
        aoConfirmar={() => excluir.mutate()}
        titulo={t('salvos.excluir')}
        texto={t('salvos.excluirTexto', { nome: salvo.nome })}
        perigoso
        carregando={excluir.isPending}
      />
    </li>
  );
}

/** As seções "Meus relatórios" e "Compartilhados comigo" da página de entrada (só o que a pessoa pode abrir). */
export function SecoesSalvos() {
  const { t } = useTranslation('relatorios');
  const s = useSessao();
  const q = useQuery({ queryKey: ['/relatorios/salvos'], queryFn: () => get<{ itens: Salvo[]; mais_compartilhados?: boolean }>('/relatorios/salvos'), enabled: s.pode('relatorios.ver') });
  if (!s.pode('relatorios.ver')) return null;
  const hoje = hojeNoFuso(s.workspace.fuso);
  return (
    <Estado consulta={q}>
      {(d) => (
        <>
          {(
            [
              ['meus', d.itens.filter((x) => x.meu)],
              ['compartilhados', d.itens.filter((x) => !x.meu)],
            ] as const
          ).map(([chave, itens]) =>
            itens.length ? (
              <section key={chave} className="mt-6 flex min-w-0 flex-col gap-3" data-secao-salvos={chave}>
                <h2 className="text-base font-semibold text-tinta-1">{t(`salvos.secoes.${chave}`)}</h2>
                <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {itens.map((x) => (
                    <CartaoSalvo key={x.id} salvo={x} hoje={hoje} />
                  ))}
                </ul>
                {/* Review r37 robustez (M2): passou do teto da lista, a pessoa sabe que há mais compartilhados. */}
                {chave === 'compartilhados' && d.mais_compartilhados ? <p className="text-sm text-tinta-2">{t('salvos.maisCompartilhados', { n: itens.length })}</p> : null}
              </section>
            ) : null,
          )}
        </>
      )}
    </Estado>
  );
}
